'use client';

import {
  GlobalWorkerOptions,
  getDocument,
  type PDFDocumentProxy
} from 'pdfjs-dist';

let workerConfigured = false;

function configureWorker() {
  if (workerConfigured || typeof window === 'undefined') return;
  // Bundles the worker as an asset alongside the main chunk so the URL is
  // resolved through the Next.js bundler instead of a fragile CDN.
  GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).toString();
  workerConfigured = true;
}

/**
 * Every PDF reaching this module is operator-uploaded and therefore untrusted.
 *
 * `isEvalSupported` is pdf.js's own opt-out for the code path that compiles
 * font programs and expressions with `Function`; CVE-2026-16633 was arbitrary
 * JavaScript execution on opening a crafted PDF through exactly that path.
 * The version is patched, but the flag closes the class of bug rather than the
 * instance, and nothing here needs the fast path.
 *
 * `isEvalSupported` is also what the prod CSP already implies — `script-src`
 * carries no `'unsafe-eval'` outside dev, so leaving it on buys a console error
 * instead of a feature.
 */
const UNTRUSTED_PDF_OPTIONS = {
  isEvalSupported: false
} as const;

interface PdfRenderOptions {
  /** Multiplier for the rendered canvas resolution. Default 2 for crisp text. */
  scale?: number;
  /** Output mime — `image/jpeg` is smaller, `image/png` is lossless. */
  mimeType?: 'image/jpeg' | 'image/png';
  /** JPEG quality (ignored for PNG). 0..1. */
  quality?: number;
}

export interface RenderedPdfPage {
  blob: Blob;
  pageNumber: number;
}

/**
 * Render every page of a PDF file to an image Blob in the browser.
 * Uses Mozilla's pdf.js (no native deps required on the server).
 *
 * Pass `onPage` to stream pages as they render: the callback is awaited before
 * the next page renders (backpressure), and pages are NOT accumulated into the
 * returned array. This lets callers upload-and-discard each page so a large PDF
 * never holds hundreds of blobs in memory at once. Without `onPage`, every page
 * is collected and returned as before.
 */
export async function renderPdfToImages(
  file: File | Blob,
  options: PdfRenderOptions & {
    maxPages?: number;
    onPage?: (page: RenderedPdfPage) => void | Promise<void>;
  } = {}
): Promise<RenderedPdfPage[]> {
  const {
    scale = 2,
    mimeType = 'image/jpeg',
    quality = 0.92,
    maxPages,
    onPage
  } = options;

  configureWorker();

  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = getDocument({
    data: arrayBuffer,
    ...UNTRUSTED_PDF_OPTIONS
  });
  const pdf: PDFDocumentProxy = await loadingTask.promise;

  const results: RenderedPdfPage[] = [];

  try {
    const lastPage =
      typeof maxPages === 'number' && maxPages > 0
        ? Math.min(pdf.numPages, maxPages)
        : pdf.numPages;
    for (let pageNumber = 1; pageNumber <= lastPage; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Could not get 2D context on canvas');
      }

      await page.render({ canvasContext: context, viewport, canvas }).promise;

      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
          mimeType,
          quality
        );
      });

      if (onPage) {
        // Stream mode: hand the page off and let the caller free it; don't
        // retain it so memory stays bounded for very large PDFs.
        await onPage({ blob, pageNumber });
      } else {
        results.push({ blob, pageNumber });
      }

      page.cleanup();
    }
  } finally {
    await loadingTask.destroy();
  }

  return results;
}

/** Read a PDF's page count without rendering. */
export async function getPdfPageCount(file: File | Blob): Promise<number> {
  configureWorker();
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = getDocument({
    data: arrayBuffer,
    ...UNTRUSTED_PDF_OPTIONS
  });
  const pdf = await loadingTask.promise;
  const count = pdf.numPages;
  await loadingTask.destroy();
  return count;
}
